import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Loader2Icon, PaperclipIcon, XIcon } from 'lucide-react';
import { products } from '../../data/products';
import { useQuoteForm, QuoteValues } from '../../hooks/useQuoteForm';
import { FormField } from './FormField';
interface QuoteFormProps {
  initialProduct?: string;
}
const baseInput = 'w-full rounded-xl border bg-ink px-4 text-[15px] text-chalk placeholder:text-chalk/35 transition-colors duration-150 focus:border-flame focus:outline-none focus:ring-2 focus:ring-flame/30';
const chevron = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23FF6A13' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")";
export function QuoteForm({
  initialProduct = ''
}: QuoteFormProps) {
  const {
    values,
    file,
    fileKey,
    errors,
    status,
    setField,
    handleFile,
    clearFile,
    handleSubmit,
    reset
  } = useQuoteForm(initialProduct);
  const inputClass = (key: keyof QuoteValues) => `${baseInput} h-12 ${errors[key] ? 'border-danger' : 'border-edge'}`;
  const describedBy = (key: keyof QuoteValues) => errors[key] ? `quote-${key}-error` : undefined;
  return <AnimatePresence mode="wait" initial={false} data-id="element-231" data-mp="3c3h413o7m8j2">
      {status === 'success' ? <motion.div key="success" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="flex min-h-[420px] flex-col items-start justify-center" role="status" data-id="element-232" data-mp="3g5lxo2enr55p">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-flame text-ink" data-id="element-233" data-mp="2t78vnqzwhhjj">
            <CheckIcon className="h-8 w-8" aria-hidden="true" data-id="element-234" data-mp="2x7jcjyp8dsd6" />
          </span>
          <h3 className="mt-6 font-display text-5xl font-bold uppercase text-chalk" data-id="element-235" data-mp="rfmcgbm92qxm">Thank you.</h3>
          <p className="mt-3 max-w-md text-lg text-muted" data-id="element-236" data-mp="velenoglk9ld">
            Our team has received your enquiry and will reply by email or phone.
          </p>
          <button type="button" onClick={reset} className="mt-8 border-b-2 border-flame pb-1 font-bold uppercase tracking-[0.06em] text-chalk transition-colors duration-150 hover:text-flame" data-id="element-237" data-mp="1zdqh9nptz6ym">
            Send another enquiry
          </button>
        </motion.div> : <motion.form key="form" onSubmit={handleSubmit} noValidate initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="grid gap-x-5 gap-y-6 sm:grid-cols-2" data-id="element-238" data-mp="2n3kp1qvam5c0">
          <FormField id="quote-name" label="Name" required error={errors.name} data-id="element-239" data-mp="29av3tonl94fy">
            <input id="quote-name" type="text" autoComplete="name" value={values.name} onChange={setField('name')} className={inputClass('name')} aria-invalid={!!errors.name} aria-describedby={describedBy('name')} data-id="element-240" data-mp="36rtuv3pre9tz" />
          </FormField>
          <FormField id="quote-company" label="Company" data-id="element-241" data-mp="39dkqmt9az54p">
            <input id="quote-company" type="text" autoComplete="organization" value={values.company} onChange={setField('company')} className={inputClass('company')} data-id="element-242" data-mp="3ea1gpt02btkf" />
          </FormField>
          <FormField id="quote-phone" label="Phone" required error={errors.phone} data-id="element-243" data-mp="3nbrf5isd3srp">
            <input id="quote-phone" type="tel" autoComplete="tel" value={values.phone} onChange={setField('phone')} placeholder="+971" className={inputClass('phone')} aria-invalid={!!errors.phone} aria-describedby={describedBy('phone')} data-id="element-244" data-mp="3sb454cej5fyi" />
          </FormField>
          <FormField id="quote-email" label="Email" required error={errors.email} data-id="element-245" data-mp="atuoqjr0fkrh">
            <input id="quote-email" type="email" autoComplete="email" value={values.email} onChange={setField('email')} className={inputClass('email')} aria-invalid={!!errors.email} aria-describedby={describedBy('email')} data-id="element-246" data-mp="avxed2o93bvo" />
          </FormField>
          <FormField id="quote-product" label="Product" required error={errors.product} className="sm:col-span-2" data-id="element-247" data-mp="66qzhdcpyyuq">
            <select id="quote-product" value={values.product} onChange={setField('product')} className={`${inputClass('product')} appearance-none bg-[length:16px] bg-[right_16px_center] bg-no-repeat pr-11 [&>option]:bg-coal`} style={{
          backgroundImage: chevron
        }} aria-invalid={!!errors.product} aria-describedby={describedBy('product')} data-id="element-248" data-mp="2688tmmkbyu4">
              <option value="" data-id="element-249" data-mp="3febjxusyeyjs">Select a product</option>
              {products.map(product => <option key={product.slug} value={product.name} data-id="element-250" data-mp="3tdktwcqkgwfb">
                  {product.name}
                </option>)}
            </select>
          </FormField>
          <FormField id="quote-quantity" label="Quantity" data-id="element-251" data-mp="18lhjlpr9twej">
            <input id="quote-quantity" type="text" value={values.quantity} onChange={setField('quantity')} placeholder="e.g. 500 rolls" className={inputClass('quantity')} data-id="element-252" data-mp="13ng1p85l034n" />
          </FormField>
          <FormField id="quote-location" label="Delivery location" data-id="element-253" data-mp="2qjxech1wcjoo">
            <input id="quote-location" type="text" value={values.location} onChange={setField('location')} placeholder="City, country" className={inputClass('location')} data-id="element-254" data-mp="2qi2ruyaz8jih" />
          </FormField>
          <FormField id="quote-message" label="Message" className="sm:col-span-2" data-id="element-255" data-mp="2hllhvyecmrob">
            <textarea id="quote-message" rows={4} value={values.message} onChange={setField('message')} placeholder="Size, grade, thickness, application…" className={`${baseInput} resize-y border-edge py-3`} data-id="element-256" data-mp="359v6t96uzvjo" />
          </FormField>

          <div className="sm:col-span-2" data-id="element-257" data-mp="30hjuf6uxmha8">
            <span className="block text-xs font-bold uppercase tracking-[0.12em] text-muted" data-id="element-258" data-mp="30gih0vqg1ipc">Attach spec sheet</span>
            {file ? <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-flame bg-steel px-4 py-3" data-id="element-259" data-mp="3sz60k6pskpob">
                <span className="flex min-w-0 items-center gap-2 text-sm text-chalk" data-id="element-260" data-mp="6o77xn127fye">
                  <PaperclipIcon className="h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-261" data-mp="1pw1zndvxplg" />
                  <span className="truncate" data-id="element-262" data-mp="3svnq6h83qs5m">{file.name}</span>
                </span>
                <button type="button" onClick={clearFile} aria-label="Remove attached file" className="inline-flex h-8 w-8 items-center justify-center rounded-full text-chalk transition-colors duration-150 hover:bg-ink" data-id="element-263" data-mp="3nxm3znfdqcpx">
                  <XIcon className="h-4 w-4" data-id="element-264" data-mp="3f4aa5d9ekcfc" />
                </button>
              </div> : <label htmlFor="quote-file" className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-edge px-4 py-4 text-sm text-muted transition-colors duration-150 focus-within:ring-2 focus-within:ring-flame/30 hover:border-flame" data-id="element-265" data-mp="3a6h5wprntshp">
                <PaperclipIcon className="h-4 w-4 text-flame" aria-hidden="true" data-id="element-266" data-mp="2w7hc73paineo" />
                <span data-id="element-267" data-mp="1box6xylvfack">
                  <span className="font-semibold text-chalk" data-id="element-268" data-mp="1bnlz9npb1ig6">Choose a file</span> — PDF, image or drawing
                </span>
                <input key={fileKey} id="quote-file" type="file" onChange={handleFile} className="sr-only" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.dwg" data-id="element-269" data-mp="1frldu0r0y76w" />
              </label>}
          </div>

          <div className="sm:col-span-2" data-id="element-270" data-mp="s0ob9el4cm8b">
            <button type="submit" disabled={status === 'submitting'} className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-flame px-10 font-bold uppercase tracking-[0.06em] text-ink transition-colors duration-150 hover:bg-chalk disabled:cursor-wait disabled:opacity-80 sm:w-auto" data-id="element-271" data-mp="s1zg7hqaw010">
              {status === 'submitting' ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-272" data-mp="995a7danni8x" />
                  Sending…
                </> : 'Send Enquiry'}
            </button>
          </div>
        </motion.form>}
    </AnimatePresence>;
}